// Push Notification Utility Functions

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Convert base64 URL-safe string to Uint8Array
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/\-/g, '+')
    .replace(/_/g, '/');
  
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

// Get visitor ID from localStorage or create new one
function getVisitorId() {
  let visitorId = localStorage.getItem('visitorId');
  if (!visitorId) {
    visitorId = 'visitor_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    localStorage.setItem('visitorId', visitorId);
  }
  return visitorId;
}

// Register service worker
export async function registerServiceWorker() {
  if (!('serviceWorker' in navigator)) {
    console.log('Service Worker not supported');
    return null;
  }
  
  try {
    const registration = await navigator.serviceWorker.register('/service-worker.js');
    console.log('Service Worker registered:', registration);
    return registration;
  } catch (error) {
    console.error('Service Worker registration failed:', error);
    return null;
  }
}

// Request notification permission
export async function requestNotificationPermission() {
  if (!('Notification' in window)) {
    console.log('This browser does not support notifications');
    return false;
  }
  
  try {
    const permission = await Notification.requestPermission();
    return permission === 'granted';
  } catch (error) {
    console.error('Error requesting notification permission:', error);
    return false;
  }
}

// Subscribe to push notifications
export async function subscribeToPushNotifications() {
  try {
    // Check if already subscribed
    const isSubscribed = localStorage.getItem('pushSubscribed');
    if (isSubscribed === 'true') {
      console.log('Already subscribed to push notifications');
      return true;
    }
    
    // Register service worker
    const registration = await registerServiceWorker();
    if (!registration) {
      return false;
    }
    
    // Request permission
    const hasPermission = await requestNotificationPermission();
    if (!hasPermission) {
      console.log('Notification permission denied');
      return false;
    }
    
    // Get VAPID public key from server
    const vapidResponse = await fetch(`${API_URL}/api/push-notifications/vapid-public-key`);
    const { publicKey } = await vapidResponse.json();
    
    // Subscribe to push
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey)
    });
    
    // Send subscription to server
    const visitorId = getVisitorId();
    const subscriptionResponse = await fetch(`${API_URL}/api/push-notifications/subscribe`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('p256dh')))),
          auth: btoa(String.fromCharCode.apply(null, new Uint8Array(subscription.getKey('auth'))))
        },
        visitor_id: visitorId,
        user_agent: navigator.userAgent
      })
    });
    
    if (subscriptionResponse.ok) {
      localStorage.setItem('pushSubscribed', 'true');
      console.log('Successfully subscribed to push notifications');
      return true;
    } else {
      console.error('Failed to save subscription on server');
      return false;
    }
  } catch (error) {
    console.error('Error subscribing to push notifications:', error);
    return false;
  }
}

// Check if user is already subscribed
export function isPushSubscribed() {
  return localStorage.getItem('pushSubscribed') === 'true';
}

// Initialize push notifications (call this on app load)
export async function initializePushNotifications() {
  // Only prompt for permission on first visit
  const hasPrompted = localStorage.getItem('pushPrompted');
  if (!hasPrompted) {
    // Show custom prompt or directly request permission
    // For now, we'll automatically request on first visit
    await subscribeToPushNotifications();
    localStorage.setItem('pushPrompted', 'true');
  }
}
